// Módulo "Ajuste de Numeração da NF-e" (a pedido do usuário).
//
// Lê e altera a numeração da NF-e (série/número) da empresa emitente no PlugNotas/Tecnospeed
// via GET/PATCH /empresa/{cnpj} (campo nfe.config.numeracao[{serie, numero}]) e mantém um log
// no schema dinâmico `AjusteNumeracaoNFe` (quando, quem, anterior, novo, status, retorno).
//
// Semântica confirmada com o usuário: `numero` é o número que SAI na próxima nota. O PlugNotas
// também devolve `numeracaoAtual` (contador interno, só anda quando uma nota é emitida) — a
// validação "não permitir número menor" usa o MAIOR entre os dois.
//
// Convenção do projeto: nada aqui lança — todo método devolve `{ ok, ... }` e a mensagem de erro.

import axios from "axios";
import EntityService from "@/services/entityService";
import authService from "@/services/authService";

const baseUrl = process.env.VUE_APP_PLUGNOTAS_API_URL || "https://api.plugnotas.com.br";
const apiKey = process.env.VUE_APP_PLUGNOTAS_API_X_API_KEY;

export const CNPJ_PADRAO = "02823203000103";
export const SERIE_PADRAO = 1;

const logService = new EntityService("AjusteNumeracaoNFe");

const http = () => axios.create({
  baseURL: baseUrl,
  headers: { "X-API-KEY": apiKey, "Content-Type": "application/json" },
  validateStatus: () => true,
  timeout: 30000,
});

const soDigitos = (v) => String(v || "").replace(/\D/g, "");

// Lê a numeração atual da série. Retorna { ok, numero, numeracaoAtual, proximo, producao, config }.
export async function lerNumeracao(cnpj = CNPJ_PADRAO, serie = SERIE_PADRAO) {
  try {
    const res = await http().get(`/empresa/${soDigitos(cnpj)}`);
    if (res.status !== 200) {
      return { ok: false, erro: `PlugNotas retornou ${res.status}: ${JSON.stringify(res.data).slice(0, 400)}` };
    }
    const config = res.data?.nfe?.config || {};
    const item = (config.numeracao || []).find((n) => Number(n.serie) === Number(serie));
    if (!item) return { ok: false, erro: `Série ${serie} não encontrada na empresa.` };
    const numero = Number(item.numero);
    const numeracaoAtual = Number(item.numeracaoAtual ?? item.numero);
    return {
      ok: true,
      numero,
      numeracaoAtual,
      proximo: Math.max(numero, numeracaoAtual),
      producao: !!config.producao,
      numeracaoAutomatica: config.numeracaoAutomatica !== false,
      config,
    };
  } catch (e) {
    return { ok: false, erro: e?.message || String(e) };
  }
}

// Valida o número novo contra o que está no PlugNotas. Retorna mensagem de erro ou null.
export function validarNovoNumero(novo, leitura) {
  const n = Number(novo);
  if (!Number.isInteger(n) || n <= 0) return "Informe um número inteiro maior que zero.";
  if (!leitura?.ok) return "Não foi possível ler a numeração atual no PlugNotas.";
  if (n <= leitura.proximo) {
    return `O número novo deve ser MAIOR que ${leitura.proximo} (número atual no PlugNotas). Diminuir a numeração não é permitido.`;
  }
  return null;
}

async function usuarioAtual() {
  try {
    const u = await authService.getUserParams();
    return u?.user || u?.name || "";
  } catch (e) {
    return "";
  }
}

async function gravarLog(doc) {
  try {
    // O backend valida o schema: campos numéricos não aceitam null (erro "#/numeroConfirmado"),
    // então campos nulos/indefinidos são omitidos do payload.
    const payload = Object.fromEntries(Object.entries(doc).filter(([, v]) => v !== null && v !== undefined));
    const res = await logService.insertOrUpdate(payload);
    const ok = res?.status === 200 && res?.data?.success === true;
    const id = doc.id || (typeof res?.data?.data === "string" ? res.data.data : res?.data?.data?.id);
    return { ok, id, res };
  } catch (e) {
    return { ok: false, erro: e?.message || String(e) };
  }
}

function resumirConfig(config) {
  // Tudo de nfe.config EXCETO a numeração — usado para garantir que o PATCH não alterou mais nada.
  const { numeracao, ...resto } = config || {}; // eslint-disable-line no-unused-vars
  return JSON.stringify(resto);
}

// Executa o ajuste: valida -> grava log Pendente -> PATCH -> relê -> confirma -> atualiza log.
// `logExistente` (opcional) = registro com status Erro para "Tentar novamente".
export async function ajustarNumeracao({ cnpj = CNPJ_PADRAO, serie = SERIE_PADRAO, numeroNovo, motivo = "", logExistente = null }) {
  const antes = await lerNumeracao(cnpj, serie);
  const erroValidacao = validarNovoNumero(numeroNovo, antes);
  if (erroValidacao) return { ok: false, erro: erroValidacao, leitura: antes };

  const novo = Number(numeroNovo);
  const usuario = await usuarioAtual();
  const agora = new Date().toISOString();

  const doc = {
    ...(logExistente || {}),
    empresaCnpj: soDigitos(cnpj),
    serie: Number(serie),
    numeroAnterior: antes.proximo,
    numeroNovo: novo,
    numeroConfirmado: null,
    status: "Pendente",
    retornoApi: "",
    motivo: motivo || logExistente?.motivo || "",
    usuario,
    dataSolicitacao: agora,
    tentativas: (Number(logExistente?.tentativas) || 0) + 1,
  };

  const logInicial = await gravarLog(doc);
  if (!logInicial.ok || !logInicial.id) {
    // Sem log não alteramos a numeração: o histórico é parte do requisito.
    return { ok: false, erro: "Não foi possível gravar o log do ajuste; a numeração NÃO foi alterada.", leitura: antes };
  }
  doc.id = logInicial.id;

  let status = "Erro";
  let retorno = "";
  let confirmado = null;
  try {
    const res = await http().patch(`/empresa/${soDigitos(cnpj)}`, {
      nfe: { config: { numeracao: [{ serie: Number(serie), numero: novo }] } },
    });
    retorno = `HTTP ${res.status}: ${typeof res.data === "string" ? res.data : JSON.stringify(res.data)}`.slice(0, 1500);

    const depois = await lerNumeracao(cnpj, serie);
    if (depois.ok) {
      confirmado = depois.numero;
      const configMudou = resumirConfig(antes.config) !== resumirConfig(depois.config);
      if (res.status >= 200 && res.status < 300 && depois.numero === novo) {
        status = "Sucesso";
        if (configMudou) retorno += " | ATENÇÃO: outras configurações de nfe.config mudaram após o PATCH, verifique.";
      } else {
        retorno += ` | Confirmação falhou: PlugNotas mostra numero=${depois.numero}, esperado ${novo}.`;
      }
    } else {
      retorno += ` | Não foi possível confirmar: ${depois.erro}`;
    }
  } catch (e) {
    retorno = `Falha na chamada: ${e?.message || e}`;
  }

  doc.status = status;
  doc.retornoApi = retorno;
  doc.numeroConfirmado = confirmado;
  const logFinal = await gravarLog(doc);

  return {
    ok: status === "Sucesso",
    erro: status === "Sucesso" ? null : retorno,
    status,
    retorno,
    numeroConfirmado: confirmado,
    logId: doc.id,
    logGravado: logFinal.ok,
  };
}

export async function listarHistorico(limit = 50) {
  try {
    const res = await logService.getAll(1, limit, null, null, null, null, null, "createAt");
    const lista = res?.data?.data?.data || res?.data?.data || [];
    return (Array.isArray(lista) ? lista : []).sort((a, b) => new Date(b.dataSolicitacao || b.createAt || 0) - new Date(a.dataSolicitacao || a.createAt || 0));
  } catch (e) {
    return [];
  }
}

export default { lerNumeracao, validarNovoNumero, ajustarNumeracao, listarHistorico, CNPJ_PADRAO, SERIE_PADRAO };
